import React, { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip, useTheme, useMediaQuery, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import youtubeImg from '@/assets/images/logo/youtube.png';
import instagramImg from '@/assets/images/logo/instagram.svg';
import wechatImg from '@/assets/images/logo/wechat.svg';
import tiktokImg from '@/assets/images/logo/tiktok.png';
import facebookImg from '@/assets/images/logo/facebook.svg';
import kakaotalkImg from '@/assets/images/logo/kakaotalk.png';
import kakaochannelImg from '@/assets/images/logo/kakaochannel.png';
import navertalkImg from '@/assets/images/logo/navertalk.png';
import naverblogImg from '@/assets/images/logo/naverblog.svg';
import kakaostoryImg from '@/assets/images/logo/kakaostory.png';

// SNS 아이콘 컴포넌트들
const SNSIcon = ({ platform, size = 24 }: { platform: string; size?: number }) => {
    const iconStyle = {
        width: size,
        height: size,
        objectFit: 'contain' as const,
        display: 'block',
        margin: 'auto',
    };

    const getIconSrc = (platform: string) => {
        switch (platform) {
            case 'youtube':
                return youtubeImg;
            case 'instagram':
                return instagramImg;
            case 'wechat':
                return wechatImg;
            case 'tiktok':
                return tiktokImg;
            case 'facebook':
                return facebookImg;
            case 'navertalk':
                return navertalkImg;
            case 'naverblog':
                return naverblogImg;
            case 'kakaotalk':
                return kakaotalkImg;
            case 'kakaochannel':
                return kakaochannelImg;
            case 'kakaostory':
                return kakaostoryImg;
            default:
                return null;
        }
    };

    const iconSrc = getIconSrc(platform);

    if (!iconSrc) {
        return (
            <svg viewBox="0 0 24 24" style={iconStyle}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
        );
    }

    return <img src={iconSrc} alt={platform} style={iconStyle} />;
};

// 플랫폼별 라벨 매핑
const getPlatformLabel = (platform: string): string => {
    const labels: { [key: string]: string } = {
        youtube: '유튜브',
        instagram: '인스타그램',
        wechat: '위챗',
        tiktok: '틱톡',
        facebook: '페이스북',
        navertalk: '네이버톡톡',
        naverblog: '네이버블로그',
        kakaotalk: '카카오톡',
        kakaochannel: '카카오채널',
        kakaostory: '카카오스토리',
    };
    return labels[platform] || platform;
};

// 플랫폼별 색상 매핑 (호버 효과용)
const getPlatformColor = (platform: string): string => {
    const colors: { [key: string]: string } = {
        youtube: '#FF0000',
        instagram: '#E4405F',
        wechat: '#07C160',
        tiktok: '#000000',
        facebook: '#1877F2',
        kakaotalk: '#FEE500',
        kakaochannel: '#FEE500',
        navertalk: '#03C75A',
        naverblog: '#03C75A',
        kakaostory: '#FEE500',
    };
    return colors[platform] || '#666666';
};

interface SNSFloatingBarProps {
    snsInfo: Array<{
        platform: string;
        url: string;
        name: string;
    }>;
}

const FloatingContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    right: theme.spacing(2),
    bottom: theme.spacing(20), // 상단이동 버튼 위에 배치
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.2),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    [theme.breakpoints.down('sm')]: {
        right: theme.spacing(1.4),
        bottom: theme.spacing(18),
        gap: theme.spacing(1.5),
        padding: theme.spacing(0.8),
        borderRadius: theme.spacing(1.5),
    },
}));

const SNSButton = styled(IconButton)(({ theme }) => ({
    width: 44,
    height: 44,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(224, 224, 224, 0.6)',
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
    margin: 0,
    padding: theme.spacing(1),
    '&:hover': {
        transform: 'translateY(-2px) scale(1.05)',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        backgroundColor: '#fafafa',
    },
    '&:active': {
        transform: 'translateY(0) scale(0.98)',
        transition: 'all 0.1s ease',
    },
    [theme.breakpoints.down('sm')]: {
        width: 32,
        height: 32,
        minWidth: 32,
        minHeight: 32,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5),
    },
}));

const SNSFloatingBar: React.FC<SNSFloatingBarProps> = ({ snsInfo }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isExpanded, setIsExpanded] = useState(true);

    if (!snsInfo || snsInfo.length === 0) {
        return null;
    }

    const shouldShowCollapseButton = snsInfo.length > 1;
    const hiddenCount = shouldShowCollapseButton ? snsInfo.length : 0;

    const toggleExpanded = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleSNSClick = (platform: string, url: string) => {
        // 새 탭에서 열기
        window.open(url, '_blank', 'noopener,noreferrer');

        // 추후 분석 이벤트 추가 가능
        // analytics.track('sns_click', { platform, url });
    };

    return (
        <FloatingContainer>
            {/* SNS 아이콘들 - 접기 버튼이 없거나 펼친 상태일 때만 표시 */}
            {(!shouldShowCollapseButton || isExpanded) && (
                <Collapse in={!shouldShowCollapseButton || isExpanded} timeout={300}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: isMobile ? theme.spacing(1.5) : theme.spacing(1.2),
                        }}
                    >
                        {snsInfo.map((sns, index) => (
                            <Tooltip
                                key={`sns-${sns.platform}-${index}`}
                                title={sns.name || getPlatformLabel(sns.platform)}
                                placement="left"
                                arrow
                            >
                                <SNSButton
                                    onClick={() => handleSNSClick(sns.platform, sns.url)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                            borderColor: getPlatformColor(sns.platform),
                                            transform: 'translateY(-2px) scale(1.05)',
                                        },
                                    }}
                                    aria-label={`${sns.name || getPlatformLabel(sns.platform)}로 이동`}
                                >
                                    <SNSIcon platform={sns.platform} size={isMobile ? 16 : 22} />
                                </SNSButton>
                            </Tooltip>
                        ))}
                    </Box>
                </Collapse>
            )}

            {/* 접기/펼치기 버튼 */}
            {shouldShowCollapseButton && (
                <Tooltip title={isExpanded ? 'SNS 정보 숨기기' : 'SNS 정보 보기'} placement="left" arrow>
                    <SNSButton
                        onClick={toggleExpanded}
                        sx={{
                            backgroundColor: isExpanded ? '#eeeeee' : '#eeeeee',
                            borderStyle: 'dashed',
                            borderColor: '#d0d0d0',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                                borderColor: '#b0b0b0',
                                transform: 'translateY(-2px) scale(1.05)',
                            },
                        }}
                        aria-label={isExpanded ? 'SNS 정보 숨기기' : 'SNS 정보 보기'}
                    >
                        {isExpanded ? (
                            <ExpandLess sx={{ fontSize: isMobile ? 14 : 22, color: '#666666' }} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 0.2 }}>
                                <ExpandMore sx={{ fontSize: isMobile ? 12 : 16, color: '#666666' }} />
                                <Box
                                    sx={{
                                        fontSize: isMobile ? 6 : 10,
                                        fontWeight: 500,
                                        color: '#666666',
                                        lineHeight: 1,
                                    }}
                                >
                                    SNS
                                </Box>
                            </Box>
                        )}
                    </SNSButton>
                </Tooltip>
            )}
        </FloatingContainer>
    );
};

export default SNSFloatingBar;
